import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { settings } from '../settings';
import { RootState } from '../redux/store';

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

interface CreateContract {
    body: any;
}

interface EditContract {
    contractID: number;
    body: any;
}

interface DeregisterContract {
    contractID: number;
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const contractsApi = createApi({
    reducerPath: 'Contratos',
    tagTypes: ['Contracts', 'ContractInfo'],
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getContracts: builder.query({
            query: (params) => ({
                url: `organismos/organismos-centrales`,
                params
            }),
            providesTags: ['Contracts']
        }),
        getContractInfo: builder.query({
            query: (contractID) => ({
                url: `organismos/organismo-central/${contractID}`
            }),
            providesTags: ['ContractInfo']
        }),

        getContractsByOrganizacion: builder.query({
            query: (params) => ({
                url: `organismos/organismos-central`,
                params
            }),
            providesTags: ['Contracts']
        }),
        registerContract: builder.mutation({
            query: ({ body }: CreateContract) => ({
                url: `organismos/organismo-central`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Contracts'])
        }),
        editContract: builder.mutation({
            query: ({ contractID, body }: EditContract) => ({
                url: `organismos/organismo-central/${contractID}`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Contracts'])
        }),
        deregisterContract: builder.mutation({
            query: ({ contractID }: DeregisterContract) => ({
                url: `organismos/organismo-central/baja/${contractID}`,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Contracts'])
        }),
        editContractsBusinessType: builder.mutation({
            query: ({
                body
            }: {
                body: { idTipoEmpresa: number; idOrganismosCentrales: number[] };
            }) => ({
                url: `organismos/organismos-centrales/asignar-tipo-empresa`,
                body: body,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Contracts'])
        }),
        getContractHistory: builder.query({
            query: (contractID) => ({
                url: `organismos/contratos/${contractID}/historial`
            })
        }),
        deregisterContracts: builder.mutation({
            query: ({ body }: { body: { idsContrato: number[]; motivo: string } }) => ({
                url: `organismos/contratos`,
                body: body,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Contracts'])
        })
    })
});

export const {
    useGetContractsQuery,
    useGetContractInfoQuery,
    useGetContractsByOrganizacionQuery,
    useRegisterContractMutation,
    useEditContractMutation,
    useDeregisterContractMutation, // single
    useDeregisterContractsMutation, // massive
    useEditContractsBusinessTypeMutation,
    useGetContractHistoryQuery
} = contractsApi;
